import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { teamAvatars } from "@/mocks/homeData";

const values = [
  {
    icon: "ri-code-box-line",
    title: "Custom Software",
    description: "We build tailored software for businesses and private users — from web platforms and mobile apps to complex enterprise systems.",
  },
  {
    icon: "ri-customer-service-2-line",
    title: "IT Consulting",
    description: "We help businesses make informed technology decisions, optimize processes, and implement modern tools that drive real growth.",
  },
  {
    icon: "ri-bar-chart-box-line",
    title: "Digital Marketing",
    description: "We develop promotion strategies, strengthen brand presence, and help products find their audience using data-driven approaches.",
  },
];

export default function AboutPage() {
  return (
    <main className="min-h-screen bg-[#0d0d0d]">
      <Navbar />

      {/* Hero */}
      <section className="relative w-full min-h-[70vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20technology%20company%20office%20with%20developers%20collaborating%20around%20large%20monitors%20showing%20dashboards%20and%20code%2C%20open%20plan%20workspace%20with%20ambient%20dark%20lighting%20and%20warm%20accent%20lamps%2C%20professional%20editorial%20photography%2C%20dramatic%20moody%20atmosphere%2C%20sophisticated%20dark%20tech%20environment%2C%20UK%20office%20space&width=1400&height=700&seq=901&orientation=landscape"
            alt="Trevionix Team at Work"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-[#0d0d0d]/60 to-[#0d0d0d]/30" />
        </div>
        <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 pb-16 md:pb-24">
          <p
            className="text-[11px] uppercase tracking-[0.28em] text-[#B8422E] mb-4"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            / About Trevionix LTD
          </p>
          <h1
            className="text-4xl md:text-5xl lg:text-[64px] font-extrabold text-white leading-[1.04] tracking-[-0.025em] max-w-2xl"
            style={{ fontFamily: "'Public Sans', sans-serif" }}
          >
            Technology Partners.
            <br />
            <span className="text-[#6C7278] font-light">Results Driven.</span>
          </h1>
        </div>
      </section>

      {/* Story */}
      <section className="w-full px-6 md:px-10 lg:px-16 py-20 md:py-28">
        <div className="max-w-3xl">
          <p
            className="text-base md:text-lg text-white/55 leading-relaxed"
            style={{ fontFamily: "'Public Sans', sans-serif" }}
          >
            We are an IT company specializing in software development for businesses and private users, as well as comprehensive turnkey digital solutions. Our expertise covers several key areas: custom software development, IT consulting, and digital marketing and advertising services.
          </p>
          <p
            className="mt-5 text-base md:text-lg text-white/55 leading-relaxed"
            style={{ fontFamily: "'Public Sans', sans-serif" }}
          >
            We help companies not only create technological products but also effectively bring them to market, attract users, and scale their business. We accompany projects at all stages — from idea and analytics to development, launch, and ongoing support. Thanks to a strong team of specialists, we combine technology and marketing into a unified growth strategy.
          </p>
          <p
            className="mt-5 text-base md:text-lg text-white/55 leading-relaxed"
            style={{ fontFamily: "'Public Sans', sans-serif" }}
          >
            Our experienced project managers, developers, UI/UX designers, marketers, and QA engineers work together to deliver solutions that are both technically excellent and market-ready.
          </p>
          <p
            className="mt-5 text-base md:text-lg text-white/55 leading-relaxed"
            style={{ fontFamily: "'Public Sans', sans-serif" }}
          >
            We value speed, transparency, and efficiency — you always understand how your project is progressing and what results it brings.
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="w-full px-6 md:px-10 lg:px-16 pb-20 md:pb-28">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/[0.06]">
          {values.map((v) => (
            <div key={v.title} className="bg-[#0d0d0d] p-8 md:p-10">
              <div className="w-11 h-11 flex items-center justify-center border border-white/[0.08] mb-6" style={{ borderRadius: "4px" }}>
                <i className={`${v.icon} text-lg text-[#B8422E]`} />
              </div>
              <h3
                className="text-lg font-bold text-white tracking-[-0.01em] mb-3"
                style={{ fontFamily: "'Public Sans', sans-serif" }}
              >
                {v.title}
              </h3>
              <p
                className="text-sm text-[#6C7278] leading-relaxed"
                style={{ fontFamily: "'Public Sans', sans-serif" }}
              >
                {v.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Team */}
      <section className="w-full px-6 md:px-10 lg:px-16 pb-20 md:pb-28">
        <div className="flex flex-col md:flex-row md:items-center gap-8 md:gap-16">
          <div className="flex items-center -space-x-3">
            {teamAvatars.map((avatar, i) => (
              <div
                key={i}
                className="w-12 h-12 md:w-14 md:h-14 rounded-full overflow-hidden border-2 border-[#0d0d0d]"
              >
                <img
                  src={avatar}
                  alt={`Team member ${i + 1}`}
                  className="w-full h-full object-cover grayscale"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
          <div>
            <p
              className="text-sm text-[#6C7278]"
              style={{ fontFamily: "'Public Sans', sans-serif" }}
            >
              A team of{" "}
              <strong className="text-white font-semibold">35+ specialists</strong>{" "}
              — developers, designers, marketers, and QA engineers based in the United Kingdom.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center gap-1.5 mt-3 text-[12px] uppercase tracking-[0.14em] text-[#6C7278] hover:text-white transition-colors whitespace-nowrap"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Contact Us
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
